/**
 * Resolves whether a connection id points at an Azure Monitor ADX proxy
 * (Application Insights / Log Analytics) rather than a real Kusto cluster.
 *
 * The two endpoints speak the same query language but NOT the same
 * management-command grammar: a proxy accepts only
 * `.show capacity|cluster|database|databases|entity|function|functions|schema|tables|version`,
 * rejects any pipe after a control command, and has no materialized-view or
 * external-table concept. Callers use this to drop Object Explorer folders
 * whose listing command would return HTTP 400 on a proxy.
 *
 * Reads the connection store directly — the same pattern `databasesService`
 * already uses — because the tree builders that need this only carry a
 * `connectionId`. Falls back to the saved connection when the connection is
 * not (yet) active. Non-kusto connections are always `false`.
 */
import { isKustoProxyUrl } from '@/core/engines/kustoUrl';
import { useConnectionStore } from '../store/connectionStore';

export function isKustoProxyConnection(connectionId: string | undefined): boolean {
  if (!connectionId) return false;
  const { connections, activeConnections } = useConnectionStore.getState();
  const connection =
    activeConnections.find((c) => c.id === connectionId)?.connection ??
    connections.find((c) => c.id === connectionId);
  if (!connection || connection.engine !== 'kusto') return false;
  return isKustoProxyUrl(String(connection.server ?? ''));
}
